import { defineStore } from 'pinia';
import {
  getYears,
  getDivisions,
  getModels,
  getSelectedModel,
  getCompareDetails,
  getConfigurationDetails,
  getStyleImages,
  getOptionToggle,
} from '@/api/entities/car';

interface ModelData {
  id: number;
  name: string;
}

interface NamedEntity {
  id: number;
  name: string;
}

interface OptionDescription {
  description: string;
}

type SelectionState = 'Selected' | 'Unselected' | 'Included' | 'Required' | string;

interface ChromeOption {
  chromeOptionCode: string;
  selectionState: SelectionState;
  descriptions: OptionDescription[];
  msrp?: number;
  rgbValue?: string;
}

interface OptionGroup {
  optionKindName?: string;
  options: ChromeOption[];
}

interface ConfigurationState {
  serializedValue?: string;
  selectedChromeOptionCodes?: string[];
}

interface ModelDetails {
  modelYear?: number | string;
  divisionName?: string;
  modelName?: string;
}

interface StyleDetails {
  id?: number;
  name?: string;
  drivetrain?: string;
  bodyType?: string;
  baseMsrp?: number;
  stockPhotoUrl?: string;
  destination?: number;
  styleName?: string;
  model?: ModelDetails | string;
  configurationState?: ConfigurationState;
}

interface ConfigurationDetails {
  style?: StyleDetails;
  configuredOptionsMsrp?: number;
  configuredTotalMsrp?: number;
  options?: OptionGroup[];
  status?: string;
  conflictResolvingChromeOptionCodes?: string[];
}

interface StyleImage {
  href?: string;
  primaryColorOptionCode?: string;
}

interface CarDetails {
  baseMSRP: number;
  destination: number;
  imageURL: string;
  styleName: string;
  subStyleName: string;
  modelDetails: ModelDetails | string;
  optionsMSRP: number;
  totalMSRP: number;
}

interface CarsState {
  years: number[];
  isLoading: boolean;
  customConfigureLoading: boolean;
  customToggleImgLoading: boolean;
  isTogglingOption: boolean;
  selectedYear: number | null;
  selectedOption: string | null;
  divisions: { title: string; value: NamedEntity }[];
  models: { title: string; value: ModelData }[];
  selectedMake: NamedEntity | null;
  selectedModel: ModelData | null;
  selectedModalDetails: StyleDetails[] | null;
  modelVariantId: number | null;
  selectedColor: string | null;
  modelsCompareDetails: Record<string, unknown> | null;
  selectedVariantDetails: StyleDetails | null;
  modelConfigurationDetails: ConfigurationDetails | null;
  modelStyleImages: StyleImage[];
  carSelectedDetails: ChromeOption[];
  selectedImg: string | null;
  downPayment: number | null;
  toggleOptionData: ConfigurationDetails;
  customMsg: string;
  filteredCarDetails: OptionGroup[] | null;
  carDetails: CarDetails;
  terms: number;
  customRate: number | null;
  paymentDetails: {
    monthlyPaymentVal: number;
    cashDown: number | string;
    totalMonths: number;
  };
}

export const useCarsStore = defineStore('cars', {
  state: (): CarsState => ({
    years: [],
    isLoading: false,
    customConfigureLoading: false,
    customToggleImgLoading: false,
    isTogglingOption: false,
    filteredCarDetails: [],
    selectedYear: null,
    divisions: [],
    selectedMake: null,
    models: [],
    selectedModel: null,
    selectedModalDetails: null,
    modelVariantId: null,
    selectedColor: null,
    modelsCompareDetails: null,
    selectedVariantDetails: null,
    modelConfigurationDetails: null,
    modelStyleImages: [],
    selectedOption: null,
    selectedImg: null,
    downPayment: null,
    toggleOptionData: {},
    carSelectedDetails: [],
    customMsg: '',
    carDetails: {
      baseMSRP: 0,
      destination: 0,
      imageURL: '/images/empty.png',
      styleName: 'Unknown',
      subStyleName: 'Unknown',
      modelDetails: 'Unknown',
      optionsMSRP: 0,
      totalMSRP: 0,
    },
    terms: 36,
    customRate: null,
    paymentDetails: {
      monthlyPaymentVal: 0,
      cashDown: 0,
      totalMonths: 0,
    },
  }),
  persist: {
    storage: sessionStorage,
    omit: [
      'isLoading',
      'customConfigureLoading',
      'customToggleImgLoading',
      'isTogglingOption',
      'toggleOptionData',
    ],
    afterHydrate: ({ store }) => {
      store.isLoading = false;
      store.customConfigureLoading = false;
      store.customToggleImgLoading = false;
      store.isTogglingOption = false;
      store.toggleOptionData = {};
    },
  },
  actions: {
    async getYears() {
      this.isLoading = true;
      try {
        const response = await getYears();
        if (response.data) {
          this.$patch({ years: response.data });
        }
        this.isLoading = false;
        console.log('Years data fetched successfully');
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },
    async getDivisions(options?: { preserveSelection?: boolean }) {
      this.isLoading = true;
      try {
        const response = await getDivisions(this.selectedYear);
        if (response.data) {
          const formatData = (response.data as NamedEntity[]).map((division) => ({
            title: division.name,
            value: { id: division.id, name: division.name },
          }));
          this.$patch({ divisions: formatData });
          if (!options?.preserveSelection) {
            this.$patch({
              selectedMake: null,
              selectedModel: null,
              selectedModalDetails: null,
              modelVariantId: null,
              selectedColor: null,
            });
          }
        }
        this.isLoading = false;
        console.log('Divisions data fetched successfully');
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },
    async getModels(options?: { preserveSelection?: boolean }) {
      this.isLoading = true;
      try {
        const response = await getModels(this.selectedYear, this.selectedMake?.id || null);
        if (response.data) {
          const formatData = (response.data as ModelData[]).map((model) => ({
            title: model.name,
            value: { id: model.id, name: model.name },
          }));
          this.$patch({ models: formatData });
          if (!options?.preserveSelection) {
            this.$patch({
              selectedModel: null,
              selectedModalDetails: null,
              modelVariantId: null,
              selectedColor: null,
            });
          }
          console.log('Models data fetched successfully');
        }
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },

    async getSelectedModel() {
      this.isLoading = true;
      try {
        if (this.selectedModel) {
          const response = await getSelectedModel(this.selectedModel?.id);
          if (response.data) {
            this.$patch({ selectedModalDetails: response.data });
            console.log('Selected Modal data fetched successfully');
          }
          this.isLoading = false;
        }
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },
    async getCompareDetails() {
      try {
        if (this.selectedModel) {
          const response = await getCompareDetails(this.selectedModel?.id);
          if (response.data) {
            this.$patch({ modelsCompareDetails: response.data });
            console.log('Data fetched successfully');
          }
        }
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },
    async getConfigurationDetails() {
      this.customConfigureLoading = true;
      try {
        if (this.modelVariantId) {
          const response = await getConfigurationDetails(this.modelVariantId);
          const data = response.data as ConfigurationDetails;

          if (data) {
            this.modelConfigurationDetails = data;
            this.carDetails.baseMSRP = data?.style?.baseMsrp || 0;
            this.carDetails.destination = data?.style?.destination || 0;
            this.carDetails.styleName = data?.style?.styleName || 'Unknown';
            this.carDetails.modelDetails = data?.style?.model || 'Unknown';
            this.carDetails.optionsMSRP = data?.configuredOptionsMsrp || 0;
            this.carDetails.totalMSRP = data?.configuredTotalMsrp || 0;
            this.syncExteriorColorPreview(this.selectedColor, { commitSelection: false });

            console.log('Data fetched successfully');
          }

          this.customConfigureLoading = false;
        }
      } catch (error) {
        this.customConfigureLoading = false;
        console.error('Error', error);
      }
    },
    async resetOptions() {
      this.selectedColor = null;
      this.toggleOptionData = {};
      this.customMsg = '';
      this.selectedImg = null;

      await this.getConfigurationDetails();

      const defaultColor = this.getSelectedExteriorColorCode();
      this.selectedColor = null;
      this.syncExteriorColorPreview(defaultColor);
      await this.getStyleImages();
    },
    async getStyleImages(options?: { commitSelection?: boolean }) {
      try {
        if (this.modelVariantId) {
          const response = await getStyleImages(this.modelVariantId);
          console.log('getStyleImages', response);
          if (response.data) {
            this.$patch({ modelStyleImages: response.data as StyleImage[] });
            this.syncExteriorColorPreview(this.selectedColor, {
              commitSelection: options?.commitSelection !== false,
            });
            console.log('Data fetched successfully');
          }
        }
      } catch (error) {
        this.isLoading = false;
        console.error('Error', error);
      }
    },
    async getOptionToggle(serializedValue: string, optionCode: string) {
      if (this.isTogglingOption) return;
      const encodedSerializedState = encodeURIComponent(serializedValue);
      this.isTogglingOption = true;
      try {
        const response = await getOptionToggle(encodedSerializedState, optionCode);
        if (response.data) {
          this.$patch({
            toggleOptionData: response.data as ConfigurationDetails,
          });
        }
        return response.data as ConfigurationDetails;
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.isTogglingOption = false;
        this.customToggleImgLoading = false;
      }
    },
    getExteriorColorOptions(): ChromeOption[] {
      return (
        this.modelConfigurationDetails?.options?.find(
          (item) => item.optionKindName === 'Primary Exterior Color',
        )?.options || []
      );
    },
    resolveExteriorColor(chromeOptionCode?: string | null): ChromeOption | null {
      const colorOptions = this.getExteriorColorOptions();
      if (!colorOptions.length) return null;

      const requested = chromeOptionCode
        ? colorOptions.find((option) => option.chromeOptionCode === chromeOptionCode)
        : null;
      return (
        requested ||
        colorOptions.find((option) => option.selectionState === 'Selected') ||
        colorOptions[0] ||
        null
      );
    },
    getSelectedExteriorColorCode(): string | null {
      return this.resolveExteriorColor()?.chromeOptionCode ?? null;
    },
    syncExteriorColorPreview(
      chromeOptionCode?: string | null,
      options?: { commitSelection?: boolean },
    ) {
      const commitSelection = options?.commitSelection !== false;
      const selected = this.resolveExteriorColor(chromeOptionCode);

      if (!selected?.chromeOptionCode) {
        const fallbackImg =
          this.modelConfigurationDetails?.style?.stockPhotoUrl || this.modelStyleImages?.[0]?.href;
        if (fallbackImg) this.carDetails.imageURL = fallbackImg;
        return;
      }

      const code = selected.chromeOptionCode;
      const colorOptions = this.getExteriorColorOptions();

      if (commitSelection) {
        this.selectedColor = code;
        for (const option of colorOptions) {
          if (option.chromeOptionCode === code) {
            option.selectionState = 'Selected';
          } else if (option.selectionState === 'Selected') {
            option.selectionState = 'Unselected';
          }
        }
      }

      if (selected.descriptions?.[0]?.description) {
        this.carDetails.subStyleName = selected.descriptions[0].description;
      }

      const colorKey = String(code).split('-')[0];
      const colorImage = this.modelStyleImages?.find(
        (imgObj) => imgObj.primaryColorOptionCode === colorKey,
      );
      const href =
        colorImage?.href ||
        this.modelConfigurationDetails?.style?.stockPhotoUrl ||
        this.modelStyleImages?.[0]?.href ||
        null;
      this.selectedImg = href;
      if (href) {
        this.carDetails.imageURL = href;
        this.customMsg = colorImage?.href ? '' : 'Colorized image is not available';
      } else {
        this.customMsg = 'Colorized image is not available';
      }
    },
    enforceSingleExteriorColor(chromeOptionCode?: string | null) {
      const colorOptions = this.getExteriorColorOptions();
      if (!colorOptions.length) return;

      const code =
        chromeOptionCode ||
        colorOptions.find((option) => option.selectionState === 'Selected')?.chromeOptionCode ||
        colorOptions[0]?.chromeOptionCode;
      if (!code) return;

      for (const option of colorOptions) {
        if (option.chromeOptionCode === code) {
          option.selectionState = 'Selected';
        } else if (
          option.selectionState === 'Selected' ||
          option.selectionState === 'Included' ||
          option.selectionState === 'Required'
        ) {
          option.selectionState = 'Unselected';
        }
      }
      this.selectedColor = code;
    },
    async applyExteriorColor(chromeOptionCode: string) {
      const serializedValue =
        this.modelConfigurationDetails?.style?.configurationState?.serializedValue;
      if (!serializedValue || !chromeOptionCode) return;

      const colorOptions = this.getExteriorColorOptions();

      const alreadySelected = colorOptions.find(
        (option) =>
          option.chromeOptionCode === chromeOptionCode && option.selectionState === 'Selected',
      );

      this.customToggleImgLoading = false;
      this.enforceSingleExteriorColor(chromeOptionCode);
      this.syncExteriorColorPreview(chromeOptionCode);

      if (alreadySelected) return;

      await this.getOptionToggle(serializedValue, chromeOptionCode);

      const toggleData = this.toggleOptionData;
      const colorOptionsToggled =
        toggleData?.options?.find((group) => group.optionKindName === 'Primary Exterior Color')
          ?.options || toggleData?.options?.[0]?.options;

      if (colorOptions.length && colorOptionsToggled?.length) {
        for (const option of colorOptions) {
          const updated = colorOptionsToggled.find(
            (item) => item.chromeOptionCode === option.chromeOptionCode,
          );
          if (updated) {
            Object.assign(option, updated);
          }
        }
      }

      const nextSerialized = toggleData?.style?.configurationState?.serializedValue;
      if (nextSerialized && this.modelConfigurationDetails?.style?.configurationState) {
        this.modelConfigurationDetails.style.configurationState.serializedValue = nextSerialized;
      }

      this.enforceSingleExteriorColor(chromeOptionCode);
      this.syncExteriorColorPreview(chromeOptionCode);
      this.customToggleImgLoading = false;

      if (toggleData?.style) {
        this.carDetails.baseMSRP = toggleData.style.baseMsrp ?? this.carDetails.baseMSRP;
        this.carDetails.optionsMSRP =
          toggleData.configuredOptionsMsrp ?? this.carDetails.optionsMSRP;
        this.carDetails.totalMSRP = toggleData.configuredTotalMsrp ?? this.carDetails.totalMSRP;
        if (this.modelConfigurationDetails) {
          this.modelConfigurationDetails.configuredOptionsMsrp =
            toggleData.configuredOptionsMsrp ??
            this.modelConfigurationDetails.configuredOptionsMsrp;
          this.modelConfigurationDetails.configuredTotalMsrp =
            toggleData.configuredTotalMsrp ?? this.modelConfigurationDetails.configuredTotalMsrp;
        }
      }
    },
  },
  getters: {},
});
